/**
 * Centralized error handling utilities for the application
 * Provides consistent error handling and user-friendly error messages
 */

import { log } from './logger';

export class AppError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly isOperational: boolean;

  constructor(
    message: string,
    code: string = 'UNKNOWN_ERROR',
    statusCode: number = 500,
    isOperational: boolean = true
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    this.isOperational = isOperational;

    Error.captureStackTrace(this, this.constructor);
  }
}

export class ValidationError extends AppError {
  constructor(message: string, field?: string) {
    super(message, 'VALIDATION_ERROR', 400);
    this.name = 'ValidationError';
    if (field) {
      this.message = `${field}: ${message}`;
    }
  }
}

export class NetworkError extends AppError {
  constructor(message: string = 'Network request failed') {
    super(message, 'NETWORK_ERROR', 503);
    this.name = 'NetworkError';
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string = 'Resource') {
    super(`${resource} not found`, 'NOT_FOUND', 404);
    this.name = 'NotFoundError';
  }
}

/**
 * Error handling utility functions
 */
export const errorHandler = {
  /**
   * Safely handles async operations with proper error logging
   */
  async handleAsync<T>(
    operation: () => Promise<T>,
    context: string = 'Unknown operation'
  ): Promise<{ data: T | null; error: AppError | null }> {
    try {
      const data = await operation();
      return { data, error: null };
    } catch (error) {
      const appError = this.normalizeError(error, context);
      log.error(appError.message, context, { code: appError.code, statusCode: appError.statusCode });
      return { data: null, error: appError };
    }
  },

  /**
   * Normalizes different error types into AppError instances
   */
  normalizeError(error: unknown, context: string = 'Unknown context'): AppError {
    if (error instanceof AppError) {
      return error;
    }

    if (error instanceof Error) {
      // Handle specific error types
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return new NetworkError(`Network error in ${context}: ${error.message}`);
      }

      if (error.message.includes('not found') || error.message.includes('404')) {
        return new NotFoundError(`Resource in ${context}`);
      }

      return new AppError(
        `Error in ${context}: ${error.message}`,
        'GENERIC_ERROR',
        500
      );
    }

    return new AppError(
      `Unknown error in ${context}: ${String(error)}`,
      'UNKNOWN_ERROR',
      500
    );
  },

  /**
   * Gets user-friendly error message
   */
  getUserMessage(error: AppError): string {
    const userFriendlyMessages: Record<string, string> = {
      NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection and try again.',
      NOT_FOUND: 'The requested content could not be found.',
      VALIDATION_ERROR: 'Please check your input and try again.',
      UNKNOWN_ERROR: 'An unexpected error occurred. Please try again later.',
    };

    return userFriendlyMessages[error.code] || error.message;
  },

  /**
   * Determines if an error should be shown to the user
   */
  shouldShowToUser(error: AppError): boolean {
    return error.isOperational && error.statusCode < 500;
  },
};

/**
 * React error boundary error handler
 */
export const handleComponentError = (error: Error, errorInfo: { componentStack: string }): void => {
  log.error('React component error', 'ErrorBoundary', {
    error: error.message,
    stack: error.stack,
    componentStack: errorInfo.componentStack,
  });
};

/**
 * Global unhandled error handler
 */
export const setupGlobalErrorHandling = (): void => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    log.error('Unhandled promise rejection', 'GlobalHandler', {
      reason: event.reason,
      promise: event.promise,
    });
    
    // Prevent the default browser behavior
    event.preventDefault();
  });

  // Handle general errors
  window.addEventListener('error', (event) => {
    log.error('Global error', 'GlobalHandler', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    });
  });
};
